import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import {
  ArrowLeft,
  Database,
  Gauge,
  LayoutDashboard,
  LogOut,
  ScrollText,
  ShieldCheck,
  Trash2,
  UserCircle,
  Users
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { useAuth } from '../../hooks/useAuth';
import { DEFAULT_TAB_PATH } from '../../utils/routeManagementHelpers';

const NAV = [
  { to: '/command-center', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/command-center/team', label: 'Team', icon: Users },
  { to: '/command-center/data', label: 'Data governance', icon: Database },
  { to: '/command-center/insights', label: 'Insights', icon: Gauge },
  { to: '/command-center/audit', label: 'Audit log', icon: ScrollText },
  { to: '/command-center/trash', label: 'Trash', icon: Trash2 }
];

export default function CommandCenterLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen bg-slate-100/70">
      {/* governance sidebar — dark to distinguish the admin surface */}
      <aside className="sticky top-0 h-screen w-[248px] shrink-0 flex flex-col bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-200">
        <div className="flex items-center gap-2.5 px-4 pt-5 pb-4">
          <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 inline-flex items-center justify-center text-white shadow-lift">
            <ShieldCheck className="h-4.5 w-4.5" />
          </span>
          <div className="leading-tight">
            <p className="text-[14px] font-semibold text-white tracking-tight">Command Center</p>
            <p className="text-[11px] text-slate-400">Governance & oversight</p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate(DEFAULT_TAB_PATH)}
          className="mx-3 mb-3 inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[12.5px] font-medium text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to workspace
        </button>

        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 px-2.5 mb-1.5">
            Manage
          </p>
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-2.5 h-9 px-2.5 rounded-md text-[13px] font-medium transition-all duration-150',
                  isActive
                    ? 'bg-white/10 text-white shadow-soft'
                    : 'text-slate-400 hover:bg-white/5 hover:text-slate-100'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={cn('h-4 w-4 shrink-0', isActive ? 'text-emerald-400' : 'text-slate-500 group-hover:text-slate-300')}
                  />
                  <span className="flex-1">{item.label}</span>
                  {isActive && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* account footer */}
        <div className="px-3 py-3 border-t border-white/10">
          <NavLink
            to="/command-center/profile"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 px-2 py-2 rounded-lg transition-colors',
                isActive ? 'bg-white/10' : 'hover:bg-white/5'
              )
            }
          >
            <span className="h-8 w-8 rounded-full bg-emerald-500/20 text-emerald-300 inline-flex items-center justify-center text-[11px] font-semibold ring-2 ring-white/10">
              {(user?.username || 'sa').slice(0, 2).toUpperCase()}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[12.5px] font-semibold text-white truncate">{user?.name || user?.username}</p>
              <p className="text-[11px] text-emerald-300/80 flex items-center gap-1">
                <UserCircle className="h-3 w-3" /> {user?.roleLabel || 'Super Admin'}
              </p>
            </div>
          </NavLink>
          <button
            type="button"
            onClick={logout}
            className="mt-1.5 w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-md text-[12px] font-medium text-slate-400 hover:bg-rose-500/10 hover:text-rose-300 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* content */}
      <main className="flex-1 min-w-0 h-screen overflow-y-auto">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 animate-fade-in">
          <Outlet />
        </div>
      </main>

      <ToastContainer position="bottom-right" autoClose={2500} theme="light" newestOnTop />
    </div>
  );
}
